import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Flag, ChevronDown, ChevronUp } from 'lucide-react';

const C = { navy: '#0D1B2A', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A' };
const TYPE_LABEL = {
  answer_drift: 'Answer drift',
  draft_manipulation: 'Draft manipulation',
  excessive_requoting: 'Excessive re-quoting',
};

export const ConductChip = ({ flags }) => {
  if (!flags?.length) return null;
  const severe = flags.some(f => f.severity === 'high');
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold ml-1.5 align-middle"
      title={flags.map(f => f.detail).join('\n')} data-testid="conduct-chip"
      style={{ background: (severe ? C.red : C.amber) + '15', color: severe ? C.red : C.amber }}>
      <Flag className="w-2.5 h-2.5" />CONDUCT
    </span>
  );
};

export const ConductFlagsDetail = ({ flags }) => {
  if (!flags?.length) return null;
  return (
    <div className="rounded-sm border p-3 mb-3" style={{ borderColor: 'rgba(226,75,74,0.25)', background: 'rgba(226,75,74,0.03)' }} data-testid="conduct-flags-detail">
      <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: C.red }}>
        <Flag className="w-3 h-3" />Broker conduct flags — underwriter review recommended
      </p>
      {flags.map((f, i) => (
        <p key={i} className="text-[11px] py-0.5" style={{ color: C.navy }}>
          <span className="px-1.5 py-px rounded text-[9px] font-bold mr-1.5"
            style={{ background: (f.severity === 'high' ? C.red : C.amber) + '15', color: f.severity === 'high' ? C.red : C.amber }}>
            {TYPE_LABEL[f.type] || f.type}
          </span>
          {f.detail}
        </p>
      ))}
    </div>
  );
};

export default function ConductWatch() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get('/conduct/brokers').then(r => setData(r.data)).catch(() => setData(null));
  }, [api]);

  if (!data || data.flagged_submissions === 0) return null;
  return (
    <div className="rounded-md border mb-4" style={{ borderColor: 'rgba(226,75,74,0.25)', background: 'rgba(226,75,74,0.03)' }} data-testid="conduct-watch">
      <button onClick={() => setOpen(!open)} className="w-full px-4 py-2.5 flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs font-semibold" style={{ color: C.red }}>
          <Flag className="w-3.5 h-3.5" />
          Conduct Watch — {data.flagged_submissions} flagged submission{data.flagged_submissions === 1 ? '' : 's'} across {data.brokers.length} broker{data.brokers.length === 1 ? '' : 's'} (90 days)
        </span>
        {open ? <ChevronUp className="w-4 h-4" style={{ color: C.slate }} /> : <ChevronDown className="w-4 h-4" style={{ color: C.slate }} />}
      </button>
      {open && (
        <div className="px-4 pb-3">
          {data.brokers.map(b => (
            <div key={b.broker_id} className="flex items-center gap-3 py-1.5 border-t text-xs" style={{ borderColor: 'rgba(226,75,74,0.1)' }}>
              <span className="flex-1 font-medium" style={{ color: C.navy }}>{b.broker_name}</span>
              <span style={{ color: C.slate }}>{b.flagged}/{b.submissions} quotes flagged</span>
              <span className="font-mono font-semibold" style={{ color: b.flag_rate_pct > 30 ? C.red : C.amber }}>{b.flag_rate_pct}%</span>
              <span className="text-[10px]" style={{ color: C.slate }}>
                {Object.entries(b.patterns).map(([t, n]) => `${TYPE_LABEL[t] || t} ×${n}`).join(' · ')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
